/**
 * Language Utilities
 *
 * Helper functions for language management in the application
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import i18n, { ensureBundledEnglishFallback, FALLBACK_LANGUAGE, hasTranslationResources, i18nReady, } from '../i18n';
/**
 * Available languages that have been loaded successfully
 */
let availableLanguages = [FALLBACK_LANGUAGE]; // English is always available as fallback
const normalizeLanguage = (language) => (language || FALLBACK_LANGUAGE).replace(/_/g, '-');
const getLanguageCandidates = (language, includeFallback = true) => {
    const normalizedLanguage = normalizeLanguage(language);
    const baseLanguage = normalizedLanguage.split('-')[0];
    const candidates = [normalizedLanguage];
    if (baseLanguage && baseLanguage !== normalizedLanguage) {
        candidates.push(baseLanguage);
    }
    if (includeFallback) {
        candidates.push(FALLBACK_LANGUAGE);
    }
    return [...new Set(candidates.filter(Boolean))];
};
const getLoadedTranslationLanguage = (language, includeFallback = true) => {
    var _a;
    return ((_a = getLanguageCandidates(language, includeFallback).find((candidate) => hasTranslationResources(candidate))) !== null && _a !== void 0 ? _a : null);
};
const markLanguageAsAvailable = (language) => {
    if (!availableLanguages.includes(language)) {
        availableLanguages.push(language);
    }
};
/**
 * Get list of available languages
 */
export const getAvailableLanguages = () => {
    return [...availableLanguages];
};
/**
 * Switch to a specific language
 * @param language - Language code (e.g., 'en', 'fr', 'es')
 * @returns Promise that resolves when language is loaded
 */
export const switchLanguage = (language) => __awaiter(void 0, void 0, void 0, function* () {
    const normalizedLanguage = normalizeLanguage(language);
    try {
        // First, try to load the language on-demand if not already available
        const isLoaded = yield loadLanguageOnDemand(normalizedLanguage);
        if (isLoaded) {
            yield i18n.changeLanguage(normalizedLanguage);
            console.log(`Language switched to: ${normalizedLanguage}`);
        }
        else {
            console.warn(`Language ${normalizedLanguage} not available, falling back to English`);
            yield i18n.changeLanguage(FALLBACK_LANGUAGE);
        }
    }
    catch (error) {
        console.warn(`Failed to switch to language: ${normalizedLanguage}, falling back to English`, error);
        yield i18n.changeLanguage(FALLBACK_LANGUAGE);
    }
    document.documentElement.lang =
        i18n.resolvedLanguage || i18n.language || FALLBACK_LANGUAGE;
    document.documentElement.dir = 'ltr';
});
let initialContextLanguagePromise = null;
let initialScreenLanguage = null;
/** Apply the context default once per page session, before the workspace opens. */
export const applyInitialContextLanguage = (contextLanguage) => {
    if (!initialContextLanguagePromise) {
        initialContextLanguagePromise = (() => __awaiter(void 0, void 0, void 0, function* () {
            yield i18nReady;
            const language = (contextLanguage === null || contextLanguage === void 0 ? void 0 : contextLanguage.trim()) || navigator.language || FALLBACK_LANGUAGE;
            yield switchLanguage(language);
            initialScreenLanguage = i18n.language || FALLBACK_LANGUAGE;
        }))();
    }
    // Context refreshes must preserve any subsequent dropdown selection.
    return initialContextLanguagePromise;
};
/** Restore the language actually applied at startup, including any fallback. */
export const resetToInitialLanguage = () => __awaiter(void 0, void 0, void 0, function* () {
    if (!initialContextLanguagePromise)
        return;
    yield initialContextLanguagePromise;
    if (initialScreenLanguage) {
        yield switchLanguage(initialScreenLanguage);
    }
});
/**
 * Get current language
 */
export const getCurrentLanguage = () => {
    return i18n.language || FALLBACK_LANGUAGE;
};
/**
 * Check if a language is available
 * @param language - Language code to check
 */
export const isLanguageAvailable = (language) => {
    return availableLanguages.includes(normalizeLanguage(language));
};
/**
 * Detect available languages by checking what files exist in /locales/
 * This is useful for dynamically discovering what languages are supported
 */
export const detectAvailableLanguages = () => __awaiter(void 0, void 0, void 0, function* () {
    const detected = [FALLBACK_LANGUAGE]; // English is always available
    // Get browser's default language
    const browserLang = navigator.language.split('-')[0]; // Get language code without region
    // Only check for browser's default language if it's not English
    if (browserLang && browserLang !== FALLBACK_LANGUAGE) {
        try {
            const response = yield fetch(`/locales/${browserLang}.json`, {
                method: 'HEAD',
            });
            if (response.ok) {
                detected.push(browserLang);
                console.log(`Browser language ${browserLang} is available`);
            }
            else {
                console.log(`Browser language ${browserLang} not available, using English`);
            }
        }
        catch (error) {
            console.log(`Browser language ${browserLang} not available, using English`);
        }
    }
    availableLanguages = [...new Set([...availableLanguages, ...detected])];
    return detected;
});
/**
 * Check if a specific language file exists and load it on-demand
 * @param language - Language code to check and load
 */
export const loadLanguageOnDemand = (language) => __awaiter(void 0, void 0, void 0, function* () {
    const normalizedLanguage = normalizeLanguage(language);
    ensureBundledEnglishFallback();
    // If already available, return true
    if (availableLanguages.includes(normalizedLanguage)) {
        return true;
    }
    const preloadedLanguage = getLoadedTranslationLanguage(normalizedLanguage, false);
    if (preloadedLanguage) {
        markLanguageAsAvailable(normalizedLanguage);
        return true;
    }
    try {
        // Try to load the language file
        yield i18n.loadLanguages([normalizedLanguage]);
        const loadedLanguage = getLoadedTranslationLanguage(normalizedLanguage, false);
        if (loadedLanguage) {
            markLanguageAsAvailable(normalizedLanguage);
            console.log(`Language ${normalizedLanguage} loaded successfully using ${loadedLanguage}`);
            return true;
        }
    }
    catch (error) {
        console.warn(`Failed to load language ${normalizedLanguage}:`, error);
    }
    return false;
});
/**
 * Initialize language detection on app start
 */
export const initializeLanguageDetection = () => __awaiter(void 0, void 0, void 0, function* () {
    yield detectAvailableLanguages();
    console.log('Available languages:', availableLanguages);
});
/**
 * Initialize the complete language system
 * This should be called before the app renders
 */
export const initializeLanguageSystem = () => __awaiter(void 0, void 0, void 0, function* () {
    try {
        console.log('Starting language system initialization...');
        // Wait for i18n to be initialized
        yield new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('i18n initialization timeout'));
            }, 5000); // Reduced to 5 seconds since we have a loader
            if (i18n.isInitialized) {
                console.log('i18n already initialized');
                clearTimeout(timeout);
                resolve();
            }
            else {
                console.log('Waiting for i18n to initialize...');
                const onInitialized = () => {
                    console.log('i18n initialized successfully');
                    clearTimeout(timeout);
                    i18n.off('initialized', onInitialized);
                    resolve();
                };
                i18n.on('initialized', onInitialized);
            }
        });
        const currentLang = normalizeLanguage(i18n.language || FALLBACK_LANGUAGE);
        ensureBundledEnglishFallback();
        let loadedLanguage = getLoadedTranslationLanguage(currentLang, false);
        if (!loadedLanguage) {
            console.log('Loading language resources for:', currentLang);
            yield loadLanguageOnDemand(currentLang);
            loadedLanguage = getLoadedTranslationLanguage(currentLang, false);
        }
        if (loadedLanguage) {
            markLanguageAsAvailable(currentLang);
            console.log('Language resources ready for:', loadedLanguage);
        }
        else {
            markLanguageAsAvailable(FALLBACK_LANGUAGE);
            if (!hasTranslationResources(FALLBACK_LANGUAGE)) {
                throw new Error('Bundled English fallback resources are unavailable');
            }
            const currentLanguageFamily = currentLang.split('-')[0];
            if (currentLanguageFamily !== FALLBACK_LANGUAGE) {
                console.warn(`Language ${currentLang} unavailable, falling back to bundled English translations`);
                yield i18n.changeLanguage(FALLBACK_LANGUAGE);
            }
        }
        // Detect available languages (but don't block if this fails)
        try {
            yield detectAvailableLanguages();
        }
        catch (error) {
            console.warn('Failed to detect available languages, continuing with defaults:', error);
        }
        console.log('Language system initialized successfully');
        console.log('Current language:', getCurrentLanguage());
        console.log('Available languages:', availableLanguages);
        console.log('i18n ready:', i18n.isInitialized);
        console.log('Has resources:', hasTranslationResources(i18n.language || FALLBACK_LANGUAGE));
    }
    catch (error) {
        console.error('Failed to initialize language system:', error);
        // Fallback to English if initialization fails
        try {
            yield i18n.changeLanguage(FALLBACK_LANGUAGE);
            console.log('Fallback to English successful');
        }
        catch (fallbackError) {
            console.error('Even fallback to English failed:', fallbackError);
        }
    }
});
